import {
  AddressProps,
  FinanceProfilePaymentFormsEnum,
  ProfileProps,
  QuotationProductProps,
} from "@/types";
import * as Yup from "yup";

export const schemaQuotation = (t: (key: string) => string) => ({
  status: Yup.string().required(t("requiredMessage")),
  rejectedReason: Yup.string().when("status", (status, schema) => {
    if (String(status) === "rejected") {
      return schema.required(t("requiredMessage"));
    }

    return schema;
  }),
  deliveryAddressUuid: Yup.string().required(t("requiredMessage")),
  buyerProfileUuid: Yup.string().required(t("requiredMessage")),
  paymentFormOption: Yup.string().required(t("requiredMessage")),
  creditTerm: Yup.string().when(
    "paymentFormOption",
    (paymentFormOption, schema) => {
      if (paymentFormOption[0] === FinanceProfilePaymentFormsEnum.credit) {
        return schema.required(t("requiredMessage"));
      }
      return schema.notRequired();
    }
  ),
  financingCost: Yup.string().when(
    "paymentFormOption",
    (paymentFormOption, schema) => {
      if (paymentFormOption[0] === FinanceProfilePaymentFormsEnum.credit) {
        return schema.required(t("requiredMessage"));
      }
      return schema.notRequired();
    }
  ),
  dollarExchangeRate: Yup.string().required(t("requiredMessage")),
  expiresAt: Yup.string().required(t("requiredMessage")),
  expectedDeliveryDays: Yup.string().required(t("requiredMessage")),
  firstMileFreightCost: Yup.string()
    .required(t("requiredMessage"))
    .min(0, t("requiredMessage")),
  lastMileFreightCost: Yup.string()
    .required(t("requiredMessage"))
    .min(0, t("requiredMessage")),
  customsFreightCost: Yup.string()
    .required(t("requiredMessage"))
    .min(0, t("requiredMessage")),
  totalFreightPrice: Yup.string()
    .required(t("requiredMessage"))
    .min(0, t("requiredMessage")),
  products: Yup.array().of(
    Yup.object().shape({
      price: Yup.string()
        .required(t("requiredMessage"))
        .min(0.001, t("requiredMessage")),
      cost: Yup.number()
        .required(t("requiredMessage"))
        .min(0.001, t("requiredMessage")),
      quantity: Yup.string()
        .required(t("requiredMessage"))
        .min(0.001, t("requiredMessage")),
      package: Yup.string().required(t("requiredMessage")),
    })
  ),
});

export const checkIsCompleteGeneralInformation = ({
  address,
  buyerProfile,
  paymentFormOption,
  financingCost,
  creditTerm,
}: {
  buyerProfile?: Partial<ProfileProps>;
  address?: Partial<AddressProps>;
  paymentFormOption?: string;
  financingCost?: string | null;
  creditTerm?: string | null;
}) => {
  const isCreditOptionsComplete =
    paymentFormOption === FinanceProfilePaymentFormsEnum.credit
      ? !!financingCost && !!creditTerm
      : true;

  return (
    !!address?.uuid &&
    !!buyerProfile?.uuid &&
    !!paymentFormOption &&
    isCreditOptionsComplete
  );
};

export const checkIsCompleteOrderData = ({
  expiresAt,
  expectedDeliveryDays,
  financingCost,
  purchaseOnCredit,
  totalFreightPrice,
}: {
  expiresAt?: string;
  expectedDeliveryDays?: number;
  totalFreightPrice?: number;
  purchaseOnCredit?: boolean;
  financingCost?: string | null;
  logisticTransportCompanyUuid?: string;
}) => {
  return (
    (!!expectedDeliveryDays || expectedDeliveryDays === 0) &&
    (!!totalFreightPrice || totalFreightPrice === 0) &&
    (!!expectedDeliveryDays || expectedDeliveryDays === 0) &&
    !!expiresAt &&
    !!(!purchaseOnCredit ? true : !!financingCost || financingCost === "")
  );
};

export const checkIsCompleteLogisticData = ({
  customsFreightCost,
  firstMileFreightCost,
  lastMileFreightCost,
}: {
  firstMileFreightCost?: number;
  lastMileFreightCost?: number;
  customsFreightCost?: number;
}) => {
  return (
    (!!firstMileFreightCost || firstMileFreightCost === 0) &&
    (!!lastMileFreightCost || lastMileFreightCost === 0) &&
    (!!customsFreightCost || customsFreightCost === 0)
  );
};

export const checkIsCatalogProductsPrices = (
  values: QuotationProductProps[] = []
) => {
  const productsValidation = values.map(
    (value) =>
      (!!value.quantity || value.quantity === 0) &&
      (!!value.cost || value.cost === 0) &&
      (!!value.price || value.price === 0) &&
      !!value.supplierUuid &&
      !!value.package
  );
  return {
    productsIsQuoted: productsValidation.map(
      (value, i) => value && !!values[i].catalogProductUuid
    ),
    isComplete: productsValidation.filter((value) => !value).length === 0,
  };
};

export const checkIsCompleteQuotation = ({
  generalInformation,
  orderData,
  catalogProductsPrices,
}: {
  generalInformation: boolean;
  orderData: boolean;
  catalogProductsPrices: boolean;
}) => {
  return !!generalInformation && !!orderData && !!catalogProductsPrices;
};
